import React, { useEffect, useRef, useState } from 'react';
import 'react-day-picker/dist/style.css';
import styled, { css } from 'styled-components';
import {
  format,
  convertDateToServerDateString,
  convertServerDateStringToDate,
  DATE_FNS_FORMAT,
} from '~/util/date';

import Input, { Props as InputProps } from '~/components/molecule/Input';
import { DayPicker, type SelectSingleEventHandler } from 'react-day-picker';
import { isSameDay } from 'date-fns';
import { nl } from 'date-fns/locale';
import { capitalizeFirstLetter } from '~/util/string';
import PortalledPositioner from '~/components/molecule/PortalledPositioner';
import JustificationContainer from '~/components/atom/JustificationContainer';
import useClickOutside from '~/hooks/useClickOutside';

type Props = {
  /** The label to give to the input */
  label: string;

  /** The date value as ISO string */
  value: string;

  /** Called when the date is changed, gives the date as an ISO string */
  onChange: (val: string | null) => any;

  /** Props to pass to the inputComponent */
  inputComponentProps?: InputProps;

  /** If true, the date will be set to today's date when the component is mounted */
  setInitialTodayDate?: boolean;

  /** Disable dates before today */
  disableBeforeToday?: boolean;
};

const DatePicker: React.FCC<Props> = ({
  dataTestId,
  onChange,
  value = new Date().toISOString(),
  inputComponentProps,
  label,
  setInitialTodayDate = true,
  disableBeforeToday,
  ...rest
}) => {
  const dateValue = convertServerDateStringToDate(value);

  const [selected, setSelected] = useState<Date | null>(
    dateValue ?? (setInitialTodayDate ? new Date() : null),
  );
  const pickerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [focused, setFocused] = useState(false);

  useClickOutside({
    callback: () => setFocused(false),
    allowClickInPortals: ['date-picker'],
    allowClickInRefs: [pickerRef],
  });

  const onDateSelect: SelectSingleEventHandler = date => {
    if (date) {
      setSelected(date);
      onChange(convertDateToServerDateString(date));
      setFocused(false);
    }
  };

  useEffect(() => {
    if (!dateValue) return;
    if (selected !== null && !isSameDay(selected, dateValue)) {
      setSelected(dateValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue]);

  useEffect(() => {
    // Only set initial date if setInitialTodayDate is true AND we don't have a valid value
    if (setInitialTodayDate && !dateValue) {
      onChange(convertDateToServerDateString(new Date()));
    }
  }, [setInitialTodayDate, dateValue, onChange]);

  return (
    <Container data-testid={dataTestId} {...rest}>
      <Input
        inputContainerRef={pickerRef}
        label={{ text: label }}
        placeholder={format(new Date(), DATE_FNS_FORMAT)}
        value={selected ? format(selected, DATE_FNS_FORMAT) : null}
        onClick={() => {
          setFocused(true);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        icon={{
          name: 'calendar',
          color: {
            group: 'secondary',
          },
        }}
        {...inputComponentProps}
        width="100%"
      />

      {focused && (
        <PortalledPositioner
          openerRect={pickerRef.current?.getBoundingClientRect() || null}
          listSize={{
            width: contentRef.current?.clientWidth || 0,
            height: contentRef.current?.clientHeight || 0,
          }}
          rootId="date-picker"
        >
          <JustificationContainer
            style={{ transform: 'translateX(-4rem)' }}
            backgroundColor={{ group: 'white' }}
            boxShadow="base"
            border={{ radius: 'base' }}
            ref={contentRef}
          >
            <DayPicker
              locale={{
                ...nl,
                localize: {
                  ...nl.localize,
                  // Capitilize month, only ENG capitilized by default
                  month: monthIndex => {
                    const monthNames = nl.localize.month;
                    const monthName = monthNames(monthIndex);
                    return capitalizeFirstLetter(monthName);
                  },
                },
              }}
              initialFocus={true}
              mode="single"
              defaultMonth={selected ?? undefined}
              selected={selected ?? undefined}
              onSelect={onDateSelect}
              disabled={
                disableBeforeToday
                  ? {
                      before: new Date(),
                    }
                  : undefined
              }
            />
          </JustificationContainer>
        </PortalledPositioner>
      )}
    </Container>
  );
};

const Container = styled.div<{}>(
  ({ theme }) => css`
    color: ${theme.color('text')};

    .rdp {
      --rdp-accent-color: ${theme.color('primary')};
      --rdp-background-color: ${theme.color('primary', 'translucent')};
      background-color: ${theme.color('white')};
      margin: 0;
      position: absolute;

      .rdp-month {
        padding: ${theme.space('m')};
        box-shadow: ${theme.boxShadow('around')};
      }
    }
  `,
);

export default DatePicker;
